import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { ArrowUp, ArrowDown, Minus, CalendarBlank, X, Warning, Fire, Skull, Package, Envelope, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Lightbulb, Factory } from '@phosphor-icons/react';

const fmt = (n) => n >= 1000 ? `${(n/1000).toFixed(1)}K` : n.toFixed(0);
const fmtDate = (d) => d.toISOString().split('T')[0];
const dispDate = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const Metric = ({ label, value, prefix = '', change, prev_label, sparkline }) => (  <div className="bg-white border border-[#E5E2DC] p-5 relative overflow-hidden">
    <p className="text-[10px] uppercase tracking-[0.15em] text-[#7a5e75] mb-1.5">{label}</p>
    <p className="text-2xl font-light text-[#641b4d] tracking-tight">{prefix}{typeof value === 'number' ? (value >= 1000 ? `${(value/1000).toFixed(1)}K` : value % 1 === 0 ? value : value.toFixed(2)) : value}</p>
    {change !== undefined && (
      <div className={`flex items-center gap-1 mt-1.5 text-xs ${change > 0 ? 'text-green-600' : change < 0 ? 'text-[#bc64aa]' : 'text-[#7a5e75]'}`}>
        {change > 0 ? <ArrowUp size={12} weight="bold" /> : change < 0 ? <ArrowDown size={12} weight="bold" /> : <Minus size={12} />}
        <span>{Math.abs(change).toFixed(0)}% {prev_label}</span>
      </div>
    )}
    {sparkline && sparkline.length > 1 && (
      <div className="absolute bottom-0 right-0 w-20 h-10 opacity-60 pointer-events-none">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkline}>
            <Area type="monotone" dataKey="v" stroke="#641b4d" strokeWidth={1.5} fill="#641b4d" fillOpacity={0.08} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

// Reusable section header for grouping submitted responses by bucket
const Section = ({ title, count, tone = 'neutral', description, children }) => {
  const tones = {
    new: 'bg-[#bc64aa] text-white',
    active: 'bg-[#641b4d] text-white',
    done: 'bg-green-700 text-white',
    muted: 'bg-[#E5E2DC] text-[#7a5e75]',
    neutral: 'bg-[#E5E2DC] text-[#641b4d]'
  };
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[#641b4d] font-medium">{title}</h3>
        <span className={`px-2 py-0.5 text-[10px] font-semibold ${tones[tone] || tones.neutral}`}>{count}</span>
        {description && <span className="text-[11px] text-[#7a5e75]">· {description}</span>}
      </div>
      {count === 0 ? (
        <p className="text-xs text-[#7a5e75] italic border-l-2 border-[#E5E2DC] pl-3 py-2">Nothing here yet.</p>
      ) : (
        <div className="space-y-3">{children}</div>
      )}
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [salesView, setSalesView] = useState('daily');
  const [chartMetric, setChartMetric] = useState('revenue');
  const [productSort, setProductSort] = useState('revenue');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [orders, setOrders] = useState([]);
  const [customInquiries, setCustomInquiries] = useState([]);
  const [bulkInquiries, setBulkInquiries] = useState([]);
  const [partnerInquiries, setPartnerInquiries] = useState([]);
  const [editingStock, setEditingStock] = useState({});
  const [editingPrice, setEditingPrice] = useState({});
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [checkoutSettings, setCheckoutSettings] = useState(null);
  const [settingsEdit, setSettingsEdit] = useState({ shipping_flat_rate: '', shipping_free_above: '' });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) { navigate('/'); return; }
    fetchData();
  }, [user, authLoading, tab, navigate]);

  useEffect(() => {
    if (tab === 'dashboard' && user?.is_admin) fetchData();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (tab === 'orders' && user?.is_admin) fetchData();
  }, [paymentFilter]);

  const handleRefund = async (orderId) => {
    if (!window.confirm('Refund this payment via Razorpay? This cannot be undone.')) return;
    try {
      await api.post(`/admin/orders/${orderId}/refund`);
      toast.success('Refund initiated');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refund failed');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await api.patch('/admin/settings/checkout', {
        shipping_flat_rate: parseFloat(settingsEdit.shipping_flat_rate) || 0,
        shipping_free_above: parseFloat(settingsEdit.shipping_free_above) || 0,
      });
      setCheckoutSettings(res.data);
      toast.success('Shipping settings saved');
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const handleCatalogSync = async () => {
    if (!window.confirm('This will apply the latest catalog spec to THIS environment (delete deprecated products, unify colours, set the Tall Cylindrical Planter description/price/dimensions/images). Idempotent — safe to run multiple times. Continue?')) return;
    try {
      const res = await api.post('/admin/migrate/catalog');
      const r = res.data;
      toast.success(`Catalog synced. ${r.total_products_after} products active. Tall Cylindrical: ${r.tall_cylindrical_upserted ? 'updated' : 'unchanged'}.`);
      console.log('Catalog sync result:', r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Catalog sync failed');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'dashboard') {
        const params = {};
        if (dateFrom) params.date_from = fmtDate(dateFrom);
        if (dateTo) params.date_to = fmtDate(dateTo);
        const r = await api.get('/admin/sales', { params });
        setData(r.data);
      } else if (tab === 'orders') {
        const params = paymentFilter && paymentFilter !== 'all' ? { payment_status: paymentFilter } : {};
        const [ordersRes, settingsRes] = await Promise.all([
          api.get('/admin/orders', { params }),
          api.get('/settings/checkout'),
        ]);
        setOrders(ordersRes.data);
        setCheckoutSettings(settingsRes.data);
        setSettingsEdit({
          shipping_flat_rate: String(settingsRes.data.shipping_flat_rate || 0),
          shipping_free_above: String(settingsRes.data.shipping_free_above || 0),
        });
      } else if (tab === 'custom') {
        const r = await api.get('/admin/inquiries/custom');
        setCustomInquiries(r.data);
      } else if (tab === 'bulk') {
        const r = await api.get('/admin/inquiries/bulk');
        setBulkInquiries(r.data);
      } else if (tab === 'partner') {
        const r = await api.get('/admin/inquiries/partner');
        setPartnerInquiries(r.data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleStockUpdate = async (pid) => {
    try {
      await api.patch(`/admin/products/${pid}/stock`, { stock: parseInt(editingStock[pid]) });
      toast.success('Stock updated');
      setEditingStock(p => { const n={...p}; delete n[pid]; return n; });
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const handlePriceUpdate = async (pid) => {
    const raw = editingPrice[pid];
    const val = parseFloat(raw);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid price'); return; }
    try {
      await api.patch(`/admin/products/${pid}/price`, { price: val });
      toast.success(`Price updated to ₹${val}`);
      setEditingPrice(p => { const n={...p}; delete n[pid]; return n; });
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // Derived data
  const viewData = data ? (salesView === 'daily' ? data.daily_sales : salesView === 'weekly' ? data.weekly_sales : data.monthly_sales) : [];
  const chartData = [...(viewData || [])].reverse().map(d => ({
    period: salesView === 'daily' ? d.period.slice(5) : salesView === 'monthly' ? d.period : d.period.split('-W')[1] ? `W${d.period.split('-W')[1]}` : d.period,
    revenue: d.revenue,
    orders: d.orders
  }));

  const currentP = viewData?.[0] || { revenue: 0, orders: 0 };
  const prevP = viewData?.[1] || { revenue: 0, orders: 0 };
  const pctChange = (curr, prev) => prev > 0 ? ((curr - prev) / prev) * 100 : curr > 0 ? 100 : 0;
  const periodLabel = salesView === 'daily' ? 'Today' : salesView === 'weekly' ? 'This Week' : 'This Month';
  const vsLabel = salesView === 'daily' ? 'vs yesterday' : salesView === 'weekly' ? 'vs last week' : 'vs last month';

  const sortedProducts = data ? [...data.product_sales].sort((a, b) =>
    productSort === 'units' ? b.units - a.units :
    productSort === 'margin' ? (b.margin_pct || 0) - (a.margin_pct || 0) :
    b.revenue - a.revenue
  ) : [];

  const bestSeller = sortedProducts[0]?.name;
  const highestMargin = data ? [...data.product_sales].sort((a, b) => (b.margin_pct || 0) - (a.margin_pct || 0))[0]?.name : null;

  // AOV trend: compute change between last half vs first half of the series
  const aovTrendData = (data?.aov_trend || []).map(d => ({ period: d.period, v: d.aov }));
  let aovChange;
  if (aovTrendData.length >= 2) {
    const mid = Math.floor(aovTrendData.length / 2);
    const firstHalf = aovTrendData.slice(0, mid).filter(d => d.v > 0);
    const secondHalf = aovTrendData.slice(mid).filter(d => d.v > 0);
    const avg = (arr) => arr.length ? arr.reduce((s, x) => s + x.v, 0) / arr.length : 0;
    const a = avg(firstHalf), b = avg(secondHalf);
    if (a > 0) aovChange = ((b - a) / a) * 100;
  }

  // Production signals: map by product name for quick lookup
  const prodSignalMap = {};
  (data?.production_signals || []).forEach(s => { prodSignalMap[s.name] = s; });

  return (
    <div data-testid="admin-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <h1 data-testid="admin-heading" className="text-3xl font-light tracking-tighter">Dashboard</h1>
          <div className="flex gap-1">
            {[
              { id: 'dashboard', label: 'Overview' },
              { id: 'orders', label: 'Orders' },
              { id: 'custom', label: 'Custom' },
              { id: 'bulk', label: 'Bulk' },
              { id: 'partner', label: 'Partner' },
            ].map(t => (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${tab === t.id ? 'bg-[#641b4d] text-white' : 'text-[#7a5e75] hover:text-[#641b4d]'}`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {loading && <div className="flex justify-center py-24"><div className="loading-spinner" /></div>}

        {/* === DASHBOARD === */}
        {!loading && tab === 'dashboard' && data && (
          <div>
            {/* TIME FILTERS */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex gap-1">
                {['daily', 'weekly', 'monthly'].map(v => (
                  <button key={v} data-testid={`view-${v}`} onClick={() => setSalesView(v)}
                    className={`px-4 py-1.5 text-xs uppercase tracking-wider transition-all ${salesView === v ? 'bg-[#641b4d] text-white' : 'bg-white border border-[#E5E2DC] text-[#641b4d] hover:border-[#641b4d]'}`}
                  >{v}</button>
                ))}
              </div>
              <div className="h-6 w-px bg-[#E5E2DC]" />
              {[7,14,30,90].map(d => (
                <button key={d} data-testid={`range-${d}d`} onClick={() => { const t=new Date(); const f=new Date(); f.setDate(f.getDate()-d); setDateFrom(f); setDateTo(t); }}
                  className="px-3 py-1.5 text-xs bg-white border border-[#E5E2DC] text-[#641b4d] hover:border-[#641b4d] transition-all"
                >{d}D</button>
              ))}
              <div className="h-6 w-px bg-[#E5E2DC]" />
              <div className="flex items-center gap-2">
                <Popover><PopoverTrigger asChild>
                  <button data-testid="date-from" className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E2DC] text-xs hover:border-[#641b4d]">
                    <CalendarBlank size={14} />{dateFrom ? dispDate(dateFrom) : 'From'}
                  </button>
                </PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} /></PopoverContent></Popover>
                <span className="text-xs text-[#7a5e75]">—</span>
                <Popover><PopoverTrigger asChild>
                  <button data-testid="date-to" className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E2DC] text-xs hover:border-[#641b4d]">
                    <CalendarBlank size={14} />{dateTo ? dispDate(dateTo) : 'To'}
                  </button>
                </PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={dateTo} onSelect={setDateTo} /></PopoverContent></Popover>
                {(dateFrom || dateTo) && (
                  <button data-testid="clear-range" onClick={() => { setDateFrom(null); setDateTo(null); }} className="text-[#bc64aa] p-1"><X size={16} /></button>
                )}
              </div>
            </div>

            {/* 1. TOP METRICS */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
              <Metric label={`${periodLabel} Revenue`} value={currentP.revenue} prefix="₹" change={pctChange(currentP.revenue, prevP.revenue)} prev_label={vsLabel} />
              <Metric label={`${periodLabel} Orders`} value={currentP.orders} change={pctChange(currentP.orders, prevP.orders)} prev_label={vsLabel} />
              <Metric label="Avg Order Value" value={data.avg_order_value} prefix="₹" change={aovChange} prev_label="trend" sparkline={aovTrendData} />
              <Metric label="Est. Profit" value={data.est_profit} prefix="₹" />
              <Metric label="All Time Revenue" value={data.total_revenue} prefix="₹" />
            </div>

            {/* 1b. TOP INSIGHTS (auto-generated) */}
            {data.insights && data.insights.length > 0 && (
              <div data-testid="top-insights" className="bg-[#641b4d] text-white p-6 mb-8 relative overflow-hidden">
                <div className="absolute -right-6 -top-6 opacity-10">
                  <Lightbulb size={120} weight="fill" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb size={16} weight="fill" className="text-[#bc64aa]" />
                  <h2 className="text-[10px] uppercase tracking-[0.2em] text-white/70">Top Insights · Auto-generated</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
                  {data.insights.map((ins, i) => (
                    <div key={i} data-testid={`insight-${i}`} className="flex gap-3">
                      <span className="text-2xl font-light text-[#bc64aa] leading-none shrink-0">0{i+1}</span>
                      <p className="text-sm leading-relaxed text-white/90">{ins}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2. SALES TREND CHART */}
            <div className="bg-white border border-[#E5E2DC] p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-light text-[#641b4d]">Sales Trend</h2>
                <div className="flex gap-1">
                  {['revenue', 'orders'].map(m => (
                    <button key={m} data-testid={`chart-${m}`} onClick={() => setChartMetric(m)}
                      className={`px-3 py-1 text-xs uppercase tracking-wider ${chartMetric === m ? 'bg-[#641b4d] text-white' : 'text-[#7a5e75] hover:text-[#641b4d]'}`}
                    >{m}</button>
                  ))}
                </div>
              </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#7a5e75' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#7a5e75' }} tickFormatter={v => chartMetric === 'revenue' ? `₹${fmt(v)}` : v} />
                    <Tooltip formatter={(v) => chartMetric === 'revenue' ? `₹${v.toFixed(0)}` : v} contentStyle={{ border: '1px solid #E5E2DC', borderRadius: 0, fontSize: 12 }} />
                    <Line type="monotone" dataKey={chartMetric} stroke="#641b4d" strokeWidth={2} dot={{ fill: '#641b4d', r: 3 }} activeDot={{ fill: '#bc64aa', r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-[#7a5e75] py-12 text-sm">No data for this period</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* 4. DEMAND FUNNEL */}
              <div className="bg-white border border-[#E5E2DC] p-6">
                <h2 className="text-base font-light text-[#641b4d] mb-6">Demand Funnel</h2>
                <div className="space-y-4">
                  {[
                    { label: 'Total Inquiries', value: data.funnel.inquiries, color: '#E5E2DC' },
                    { label: 'Orders Placed', value: data.funnel.orders, color: '#641b4d' },
                  ].map(step => (
                    <div key={step.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#7a5e75]">{step.label}</span>
                        <span className="font-semibold text-[#641b4d]">{step.value}</span>
                      </div>
                      <div className="h-3 bg-[#F4F4F4] overflow-hidden">
                        <div className="h-full" style={{ width: `${Math.min(100, (step.value / Math.max(data.funnel.inquiries, 1)) * 100)}%`, backgroundColor: step.color }} />
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-[#E5E2DC]">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#7a5e75]">Conversion</span>
                      <span className="font-semibold text-[#641b4d]">{data.funnel.conversion.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. CUSTOMER INSIGHTS */}
              <div className="bg-white border border-[#E5E2DC] p-6">
                <h2 className="text-base font-light text-[#641b4d] mb-6">Customers</h2>
                <div className="flex gap-4 mb-6">
                  <div className="flex-1 bg-[#F4F4F4] p-4 text-center">
                    <p className="text-2xl font-light text-[#641b4d]">{data.customers.new}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#7a5e75] mt-1">New</p>
                  </div>
                  <div className="flex-1 bg-[#F4F4F4] p-4 text-center">
                    <p className="text-2xl font-light text-[#641b4d]">{data.customers.repeat}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#7a5e75] mt-1">Repeat</p>
                  </div>
                </div>
                <h3 className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3">Top Customers</h3>
                <div className="space-y-2">
                  {data.customers.top.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-[#641b4d] truncate mr-2">{c.name}</span>
                      <span className="text-[#7a5e75] shrink-0">₹{c.revenue.toFixed(0)} ({c.orders})</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 5. INVENTORY SNAPSHOT */}
              <div className="bg-white border border-[#E5E2DC] p-6">
                <h2 className="text-base font-light text-[#641b4d] mb-6">Inventory Alerts</h2>
                <div className="space-y-3">
                  {data.inventory.filter(i => i.status !== 'ok').length === 0 ? (
                    <p className="text-sm text-green-600 py-4 text-center">All stock levels healthy</p>
                  ) : (
                    data.inventory.filter(i => i.status !== 'ok').map(item => (
                      <div key={item.id} className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${item.status === 'critical' ? 'bg-[#bc64aa]' : item.status === 'low' ? 'bg-orange-500' : 'bg-[#7a5e75]'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#641b4d] truncate">{item.name}</p>
                          <p className="text-xs text-[#7a5e75]">{item.stock} left · {item.sold_14d} sold/14d · {item.days_left < 999 ? `${item.days_left}d left` : 'No movement'}</p>
                        </div>
                        {item.status === 'critical' && <Warning size={16} className="text-[#bc64aa] shrink-0" />}
                        {item.status === 'dead' && <Skull size={16} className="text-[#7a5e75] shrink-0" />}
                      </div>
                    ))
                  )}
                  {/* Full inventory table */}
                  <div className="pt-3 border-t border-[#E5E2DC]">
                    <details>
                      <summary className="text-xs text-[#bc64aa] cursor-pointer uppercase tracking-wider">View all inventory</summary>
                      <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                        {data.inventory.map(item => (
                          <div key={item.id} className="flex justify-between items-center gap-2 text-xs">
                            <span className="text-[#641b4d] truncate flex-1 mr-2">{item.name}</span>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {/* Price editor */}
                              {editingPrice[item.id] !== undefined ? (
                                <>
                                  <span className="text-[10px] text-[#7a5e75]">₹</span>
                                  <input type="number" step="0.01" min="0" data-testid={`price-input-${item.id}`} value={editingPrice[item.id]} onChange={e => setEditingPrice({...editingPrice, [item.id]: e.target.value})} className="w-16 border border-[#E5E2DC] px-1 py-0.5 text-xs" />
                                  <button data-testid={`save-price-${item.id}`} onClick={() => handlePriceUpdate(item.id)} className="text-[10px] bg-[#641b4d] text-white px-1.5 py-0.5">Save</button>
                                  <button onClick={() => setEditingPrice(p => { const n={...p}; delete n[item.id]; return n; })} className="text-[10px] text-[#7a5e75] px-1">×</button>
                                </>
                              ) : (
                                <button data-testid={`edit-price-${item.id}`} onClick={() => setEditingPrice({...editingPrice, [item.id]: item.price ?? 0})} className="text-[#641b4d] px-1.5 py-0.5 border border-[#E5E2DC] hover:border-[#641b4d]">
                                  ₹{Number(item.price || 0).toFixed(0)}
                                </button>
                              )}
                              <span className="text-[#E5E2DC]">·</span>
                              {/* Stock editor */}
                              {editingStock[item.id] !== undefined ? (
                                <>
                                  <input type="number" value={editingStock[item.id]} onChange={e => setEditingStock({...editingStock, [item.id]: e.target.value})} className="w-14 border border-[#E5E2DC] px-1 py-0.5 text-xs" min={0} />
                                  <button onClick={() => handleStockUpdate(item.id)} className="text-[10px] bg-[#641b4d] text-white px-2 py-0.5">Save</button>
                                </>
                              ) : (
                                <>
                                  <span className={`font-semibold ${item.stock <= 5 ? 'text-[#bc64aa]' : 'text-[#641b4d]'}`}>{item.stock}</span>
                                  <button onClick={() => setEditingStock({...editingStock, [item.id]: item.stock})} className="text-[#bc64aa] text-[10px] uppercase">Edit</button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            </div>

            {/* 7. PRODUCTION QUEUE */}
            {data.production_signals && data.production_signals.length > 0 && (
              <div data-testid="production-queue" className="bg-white border border-[#E5E2DC] p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Factory size={16} className="text-[#bc64aa]" weight="fill" />
                    <h2 className="text-base font-light text-[#641b4d]">Production Queue</h2>
                    <span className="text-[10px] uppercase tracking-wider text-[#7a5e75]">· Last 7 days</span>
                  </div>
                  <p className="text-[10px] text-[#7a5e75] uppercase tracking-wider">Recommended: 2× 7d velocity · Min 10</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.production_signals.slice(0, 6).map((s) => {
                    const stockCritical = s.stock <= 5;
                    const urgent = s.sold_7d > 0 && s.stock < s.sold_7d;
                    return (
                      <div key={s.id} data-testid={`prod-signal-${s.id}`} className={`border p-4 flex items-center justify-between gap-3 ${urgent ? 'border-[#bc64aa] bg-[#FDF4F0]' : 'border-[#E5E2DC] bg-[#F4F4F4]'}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-[#641b4d] truncate">{s.name}</p>
                          <p className="text-[10px] text-[#7a5e75] uppercase tracking-wider mt-1">
                            {s.sold_7d} sold · <span className={stockCritical ? 'text-[#bc64aa]' : ''}>{s.stock} in stock</span>
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[9px] text-[#7a5e75] uppercase tracking-wider">Produce</p>
                          <p className="text-xl font-light text-[#641b4d]">{s.recommended_qty}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 3. PRODUCT PERFORMANCE */}
            <div className="bg-white border border-[#E5E2DC] p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-light text-[#641b4d]">Product Performance</h2>
                <div className="flex gap-1">
                  {['revenue', 'units', 'margin'].map(s => (
                    <button key={s} data-testid={`sort-${s}`} onClick={() => setProductSort(s)}
                      className={`px-3 py-1 text-xs uppercase tracking-wider ${productSort === s ? 'bg-[#641b4d] text-white' : 'text-[#7a5e75] hover:text-[#641b4d]'}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              {sortedProducts.length === 0 ? (
                <p className="text-center text-[#7a5e75] py-8 text-sm">No sales data yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E5E2DC]">
                        <th className="text-left text-[10px] uppercase tracking-widest text-[#7a5e75] p-3">Product</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3">Units</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3">Revenue</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3 hidden md:table-cell">Est. Cost/Unit</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3 hidden md:table-cell">Est. Profit</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3">Margin</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3 hidden lg:table-cell">7d Sold</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3 hidden lg:table-cell">Produce</th>
                        <th className="text-right text-[10px] uppercase tracking-widest text-[#7a5e75] p-3 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedProducts.map(p => (
                        <tr key={p.name} data-testid={`product-row-${p.name}`} className="border-b border-[#E5E2DC] last:border-0">
                          <td className="p-3">
                            <span className="text-sm text-[#641b4d]">{p.name}</span>
                            <div className="flex gap-1 mt-1">
                              {p.name === bestSeller && <span className="text-[9px] bg-[#641b4d] text-white px-1.5 py-0.5 uppercase tracking-wider">Best Seller</span>}
                              {p.name === highestMargin && <span className="text-[9px] bg-green-700 text-white px-1.5 py-0.5 uppercase tracking-wider">Top Margin</span>}
                              {p.units <= 1 && <span className="text-[9px] bg-[#bc64aa] text-white px-1.5 py-0.5 uppercase tracking-wider">Slow</span>}
                            </div>
                          </td>
                          <td className="p-3 text-sm text-[#7a5e75] text-right">{p.units}</td>
                          <td className="p-3 text-sm font-semibold text-[#641b4d] text-right">₹{p.revenue.toFixed(0)}</td>
                          <td className="p-3 text-sm text-[#7a5e75] text-right hidden md:table-cell">₹{(p.est_cost || 0).toFixed(0)}</td>
                          <td className="p-3 text-sm text-right hidden md:table-cell">
                            <span className={(p.est_profit || 0) >= 0 ? 'text-green-600' : 'text-[#bc64aa]'}>₹{(p.est_profit || 0).toFixed(0)}</span>
                          </td>
                          <td className="p-3 text-sm text-right">
                            <span className={(p.margin_pct || 0) >= 50 ? 'text-green-600 font-semibold' : 'text-[#7a5e75]'}>{(p.margin_pct || 0).toFixed(0)}%</span>
                          </td>
                          <td className="p-3 text-sm text-[#7a5e75] text-right hidden lg:table-cell" data-testid={`sold-7d-${p.name}`}>
                            {prodSignalMap[p.name]?.sold_7d ?? 0}
                          </td>
                          <td className="p-3 text-sm text-right hidden lg:table-cell" data-testid={`produce-qty-${p.name}`}>
                            <span className="inline-flex items-center gap-1 text-[#641b4d] font-semibold">
                              <Factory size={11} className="text-[#bc64aa]" />
                              {prodSignalMap[p.name]?.recommended_qty ?? 10}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <div className="w-8 h-2 bg-[#F4F4F4] overflow-hidden inline-block">
                              <div className="h-full bg-[#641b4d]" style={{ width: `${Math.min(100, (p.margin_pct || 0))}%` }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === ORDERS — categorized by lifecycle === */}
        {!loading && tab === 'orders' && (() => {
          const filtered = orders; // backend already filtered
          const newOrders = filtered.filter(o => !o.order_status || ['order_placed','confirmed'].includes(o.order_status));
          const inProgress = filtered.filter(o => ['dispatched','in_transit','out_for_delivery'].includes(o.order_status));
          const completed = filtered.filter(o => ['delivered','fulfilled'].includes(o.order_status));
          const payPill = (ps) => {
            const s = ({
              paid: 'bg-green-100 text-green-800', completed: 'bg-green-100 text-green-800',
              pending: 'bg-orange-100 text-orange-800', failed: 'bg-red-100 text-red-800',
              refunded: 'bg-blue-100 text-blue-800', abandoned: 'bg-gray-100 text-gray-700', cancelled: 'bg-gray-100 text-gray-700'
            })[ps] || 'bg-gray-100 text-gray-700';
            return <span className={`px-3 py-0.5 text-[10px] uppercase tracking-wider ${s}`}>{ps || 'pending'}</span>;
          };
          const renderOrder = (order) => (
            <div key={order.id} data-testid={`order-card-${order.id}`} className="bg-white border border-[#E5E2DC] p-5">
              <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#7a5e75]">{order.order_number || order.id.slice(0,8)}</p>
                  <p className="text-sm font-semibold">{order.shipping_info.name}</p>
                  <p className="text-xs text-[#7a5e75]">{order.shipping_info.email} · {new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {payPill(order.payment_status)}
                  <select value={order.order_status || 'order_placed'} onChange={async (e) => { try { await api.patch(`/admin/orders/${order.id}/status`, { order_status: e.target.value }); toast.success('Updated'); fetchData(); } catch(er) { toast.error('Failed'); }}}
                    className="text-[10px] border border-[#E5E2DC] px-2 py-1 bg-white"
                  >
                    {['order_placed','confirmed','dispatched','in_transit','out_for_delivery','delivered','fulfilled'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                  </select>
                  {(order.payment_status === 'paid' || order.payment_status === 'completed') && (
                    <button data-testid={`refund-${order.id}`} onClick={() => handleRefund(order.id)} className="text-[10px] uppercase tracking-wider border border-[#bc64aa] text-[#bc64aa] hover:bg-[#bc64aa] hover:text-white px-2 py-1 transition-all">
                      Refund
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                {order.items.map((item, i) => <div key={i} className="flex justify-between text-xs text-[#7a5e75]"><span>{item.product_name} × {item.quantity}</span><span>₹{(item.price * item.quantity).toFixed(0)}</span></div>)}
              </div>
              <div className="mt-3 pt-3 border-t border-[#E5E2DC] flex justify-between items-center">
                <span className="text-[10px] text-[#7a5e75] truncate">{order.razorpay_payment_id || ''}</span>
                <span className="text-sm font-semibold">₹{order.total.toFixed(0)}</span>
              </div>
            </div>
          );
          return (
            <div>
              {/* Checkout settings panel */}
              <div className="bg-white border border-[#E5E2DC] p-5 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] text-[#641b4d] font-medium mb-1">Checkout Settings</h3>
                    <p className="text-[11px] text-[#7a5e75]">Shipping rules apply to all new orders. Prices remain inclusive of GST.</p>
                  </div>
                  <div className="flex items-end gap-3 flex-wrap">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#7a5e75] block mb-1">Shipping (₹)</label>
                      <input type="number" min="0" step="1" data-testid="setting-shipping-rate" value={settingsEdit.shipping_flat_rate}
                        onChange={e => setSettingsEdit(s => ({...s, shipping_flat_rate: e.target.value}))}
                        className="w-24 border border-[#E5E2DC] px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#7a5e75] block mb-1">Free above (₹)</label>
                      <input type="number" min="0" step="1" data-testid="setting-free-above" value={settingsEdit.shipping_free_above}
                        onChange={e => setSettingsEdit(s => ({...s, shipping_free_above: e.target.value}))}
                        className="w-28 border border-[#E5E2DC] px-2 py-1 text-sm" />
                    </div>
                    <button data-testid="save-checkout-settings" onClick={handleSaveSettings} className="text-xs uppercase tracking-wider bg-[#641b4d] text-white px-4 py-1.5 hover:bg-[#8c1666]">Save</button>
                  </div>
                </div>
                {checkoutSettings && (
                  <p className="text-[10px] text-[#7a5e75] mt-3">
                    Current: {Number(checkoutSettings.shipping_flat_rate) === 0 ? 'FREE shipping on all orders' :
                      (Number(checkoutSettings.shipping_free_above) > 0
                        ? `₹${checkoutSettings.shipping_flat_rate} shipping, FREE above ₹${checkoutSettings.shipping_free_above}`
                        : `Flat ₹${checkoutSettings.shipping_flat_rate} shipping on every order`)}
                  </p>
                )}
                <div className="mt-4 pt-4 border-t border-[#E5E2DC] flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h4 className="text-xs uppercase tracking-[0.2em] text-[#641b4d] font-medium mb-1">Catalog Sync</h4>
                    <p className="text-[11px] text-[#7a5e75]">Apply the latest catalog spec (product list, Tall Cylindrical Planter details, colour palettes). Idempotent.</p>
                  </div>
                  <button data-testid="sync-catalog-btn" onClick={handleCatalogSync}
                    className="text-xs uppercase tracking-wider bg-[#bc64aa] text-white px-4 py-1.5 hover:bg-[#8c1666]">
                    Sync Catalog to Latest
                  </button>
                </div>
              </div>

              {/* Payment filter */}
              <div className="flex flex-wrap gap-1 mb-6">
                {['all','pending','paid','failed','refunded','abandoned','cancelled'].map(f => (
                  <button key={f} data-testid={`pfilter-${f}`} onClick={() => setPaymentFilter(f)}
                    className={`px-3 py-1 text-[10px] uppercase tracking-wider border ${paymentFilter === f ? 'bg-[#641b4d] text-white border-[#641b4d]' : 'bg-white text-[#7a5e75] border-[#E5E2DC] hover:border-[#641b4d]'}`}>
                    {f}
                  </button>
                ))}
              </div>

              {orders.length === 0 ? (
                <p className="text-center text-[#7a5e75] py-12">No orders in this filter</p>
              ) : (
                <>
                  <Section title="New Orders" count={newOrders.length} tone="new" description="Needs confirmation or dispatch">
                    {newOrders.map(renderOrder)}
                  </Section>
                  <Section title="In Fulfillment" count={inProgress.length} tone="active" description="Dispatched · In transit · Out for delivery">
                    {inProgress.map(renderOrder)}
                  </Section>
                  <Section title="Completed" count={completed.length} tone="done" description="Delivered or fulfilled">
                    {completed.map(renderOrder)}
                  </Section>
                </>
              )}
            </div>
          );
        })()}

        {/* === CUSTOM INQUIRIES — categorized by design path === */}
        {!loading && tab === 'custom' && (() => {
          const isCustomize = (r) => (r || '').trim().startsWith('Customizing:');
          const isOwnDesign = (r) => (r || '').includes('Design file:');
          const customize = customInquiries.filter(i => isCustomize(i.requirements));
          const ownDesign = customInquiries.filter(i => !isCustomize(i.requirements) && isOwnDesign(i.requirements));
          const general = customInquiries.filter(i => !isCustomize(i.requirements) && !isOwnDesign(i.requirements));
          const renderInq = (inq) => (
            <div key={inq.id} data-testid={`custom-inq-${inq.id}`} className="bg-white border border-[#E5E2DC] p-5">
              <div className="flex justify-between mb-2">
                <div><p className="text-sm font-semibold">{inq.name}</p><p className="text-xs text-[#7a5e75]">{inq.email} · {inq.phone}</p></div>
                <span className="text-xs text-[#7a5e75]">{new Date(inq.created_at).toLocaleDateString()}</span>
              </div>
              <div className="bg-[#F4F4F4] p-3 text-sm text-[#641b4d] whitespace-pre-wrap">{inq.requirements}</div>
            </div>
          );
          return customInquiries.length === 0 ? (
            <p className="text-center text-[#7a5e75] py-12">No custom inquiries yet</p>
          ) : (
            <div>
              <Section title="Customize Existing Design" count={customize.length} tone="active" description="Picked one of our planters, tweaked color/size/texture">
                {customize.map(renderInq)}
              </Section>
              <Section title="Share Own Design" count={ownDesign.length} tone="new" description="Customer uploaded their own reference or design file">
                {ownDesign.map(renderInq)}
              </Section>
              <Section title="General Enquiries" count={general.length} tone="muted" description="Free-form questions or other custom requests">
                {general.map(renderInq)}
              </Section>
            </div>
          );
        })()}

        {/* === BULK INQUIRIES — categorized by order size === */}
        {!loading && tab === 'bulk' && (() => {
          const small = bulkInquiries.filter(i => (i.quantity || 0) < 20);
          const medium = bulkInquiries.filter(i => (i.quantity || 0) >= 20 && (i.quantity || 0) < 50);
          const large = bulkInquiries.filter(i => (i.quantity || 0) >= 50);
          const renderInq = (inq) => (
            <div key={inq.id} data-testid={`bulk-inq-${inq.id}`} className="bg-white border border-[#E5E2DC] p-5">
              <div className="flex justify-between mb-2">
                <div><p className="text-sm font-semibold">{inq.name}</p><p className="text-xs text-[#7a5e75]">{inq.email} · {inq.phone} · {new Date(inq.created_at).toLocaleDateString()}</p></div>
                <div className="text-right"><p className="text-lg font-semibold">{inq.quantity} units</p><p className="text-xs text-[#7a5e75]">{inq.product_id}</p></div>
              </div>
              {inq.message && <div className="bg-[#F4F4F4] p-3 text-sm text-[#641b4d]">{inq.message}</div>}
            </div>
          );
          return bulkInquiries.length === 0 ? (
            <p className="text-center text-[#7a5e75] py-12">No bulk inquiries yet</p>
          ) : (
            <div>
              <Section title="Large Orders" count={large.length} tone="new" description="50+ units · prioritize outreach">
                {large.map(renderInq)}
              </Section>
              <Section title="Medium Orders" count={medium.length} tone="active" description="20–49 units">
                {medium.map(renderInq)}
              </Section>
              <Section title="Small Orders" count={small.length} tone="muted" description="Under 20 units">
                {small.map(renderInq)}
              </Section>
            </div>
          );
        })()}

        {/* === PARTNER INQUIRIES — categorized by business line === */}
        {!loading && tab === 'partner' && (() => {
          const groupKey = (p) => (p.business_line || 'unspecified').toLowerCase();
          const groups = {};
          partnerInquiries.forEach(p => {
            const k = groupKey(p);
            if (!groups[k]) groups[k] = [];
            groups[k].push(p);
          });
          const renderInq = (inq) => (
            <div key={inq.id} data-testid={`partner-inq-${inq.id}`} className="bg-white border border-[#E5E2DC] p-5">
              <div className="flex justify-between mb-2 flex-wrap gap-2">
                <div>
                  <p className="text-sm font-semibold">{inq.first_name}{inq.company_name ? ` · ${inq.company_name}` : ''}</p>
                  <p className="text-xs text-[#7a5e75]">{inq.email} · {inq.phone}{inq.location ? ` · ${inq.location}` : ''}</p>
                </div>
                <span className="text-xs text-[#7a5e75]">{new Date(inq.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-[#7a5e75] mb-2">
                {inq.business_line && <span><span className="text-[#641b4d] font-medium">Line:</span> {inq.business_line}</span>}
                {inq.workforce && <span><span className="text-[#641b4d] font-medium">Team:</span> {inq.workforce}</span>}
                {inq.website && <a href={inq.website} target="_blank" rel="noreferrer" className="text-[#bc64aa] underline">{inq.website}</a>}
              </div>
              {inq.message && <div className="bg-[#F4F4F4] p-3 text-sm text-[#641b4d] whitespace-pre-wrap">{inq.message}</div>}
            </div>
          );
          return partnerInquiries.length === 0 ? (
            <p className="text-center text-[#7a5e75] py-12">No partner inquiries yet</p>
          ) : (
            <div>
              {Object.entries(groups).sort((a, b) => b[1].length - a[1].length).map(([line, items]) => (
                <Section
                  key={line}
                  title={line === 'unspecified' ? 'Unspecified Business Line' : line.charAt(0).toUpperCase() + line.slice(1)}
                  count={items.length}
                  tone={line === 'unspecified' ? 'muted' : 'active'}
                  description={`${items.length} prospective partner${items.length === 1 ? '' : 's'}`}
                >
                  {items.map(renderInq)}
                </Section>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Admin;
